<script setup>
import { ref, watch, onMounted } from 'vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import IaTab from './IaTab.vue';
import RodizioTab from './RodizioTab.vue';
import AlertasTab from './AlertasTab.vue';
import api from 'dashboard/api/aceleres/iaRodizios';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  teamId: { type: [Number, String], required: true },
});

const TABS = [
  { key: 'ia', label: 'IA' },
  { key: 'rodizio', label: 'Rodízio' },
  { key: 'alertas', label: 'Alertas' },
];

const DEFAULT_WORKING_HOURS = [0, 1, 2, 3, 4, 5, 6].map(d => ({
  day_of_week: d,
  closed_all_day: d === 0 || d === 6,
  open_hour: d === 0 || d === 6 ? null : 8,
  open_minutes: d === 0 || d === 6 ? null : 0,
  close_hour: d === 0 || d === 6 ? null : 19,
  close_minutes: d === 0 || d === 6 ? null : 0,
  open_all_day: false,
}));

const DEFAULT_SETTINGS = {
  ai_enabled: false,
  ai_inbox_id: null,
  ai_context: '',
  ai_context_source_team_id: null,
  rodizio_enabled: false,
  rodizio_idle_minutes: 4,
  rodizio_max_cycles: 1,
  rodizio_manager_user_id: null,
  rodizio_horario_mode: 'global',
  absence_alert_enabled: true,
  absence_alert_delay_minutes: 30,
  global_working_hours: DEFAULT_WORKING_HOURS,
  inboxes: [],
  managers: [],
  captain_configuration: {},
};

const activeIndex = ref(0);
const settings = ref({ ...DEFAULT_SETTINGS });
const loadError = ref(null);
const isSaving = ref(false);

const fetchSettings = async () => {
  try {
    const { data } = await api.getSettings(props.teamId);
    settings.value = { ...DEFAULT_SETTINGS, ...data };
  } catch (e) {
    loadError.value = e?.response?.data?.error || e?.message || 'Erro desconhecido';
    // eslint-disable-next-line no-console
    console.error('[ai-rodizios] falha ao carregar settings:', e);
    useAlert(`Falha ao carregar configurações: ${loadError.value}`);
  }
};

const onTabChange = tab => {
  const idx = TABS.findIndex(t => t.label === tab.label);
  if (idx !== -1) activeIndex.value = idx;
};

const saveSettings = async patch => {
  isSaving.value = true;
  try {
    const { data } = await api.updateSettings(patch, props.teamId);
    settings.value = { ...DEFAULT_SETTINGS, ...data };
    useAlert('Configurações salvas');
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao salvar configurações');
  } finally {
    isSaving.value = false;
  }
};

onMounted(fetchSettings);

// Aceleres — Fase 1: recarrega ao trocar de equipe. Defensivo (o modal já
// desmonta/remonta ao fechar e reabrir), mas garante reset do estado se o
// teamId mudar sem desmontar — nunca mostrar/salvar dados de outro time.
watch(() => props.teamId, fetchSettings);
</script>

<template>
  <div class="flex flex-col w-full gap-6">
    <div class="ai-rodizios-tabs [&_button]:!px-3 sm:[&_button]:!px-6">
      <TabBar
        :tabs="TABS.map(tab => ({ label: tab.label }))"
        :initial-active-tab="activeIndex"
        @tab-changed="onTabChange"
      />
    </div>

    <div v-if="loadError"
         class="px-4 py-3 border border-n-ruby-7 bg-n-ruby-2 text-n-ruby-11 rounded text-body-small">
      Não foi possível carregar as configurações do servidor.
      Exibindo padrões locais — suas edições serão salvas normalmente.
      <span class="ml-1 opacity-70">({{ loadError }})</span>
    </div>

    <IaTab
      v-show="activeIndex === 0"
      :settings="settings"
      :is-saving="isSaving"
      :team-id="props.teamId"
      @refresh="fetchSettings"
      @save="saveSettings"
    />
    <RodizioTab v-show="activeIndex === 1" :team-id="props.teamId" />
    <AlertasTab
      v-show="activeIndex === 2"
      :settings="settings"
      :is-saving="isSaving"
      @save="saveSettings"
    />
  </div>
</template>

<style scoped>
.ai-rodizios-tabs :deep(> div) {
  height: 3rem;
}
.ai-rodizios-tabs :deep(> div > div) {
  height: 3rem;
}
.ai-rodizios-tabs :deep(button) {
  font-size: 1rem;
  line-height: 1.5rem;
  padding: 0.5rem 1.5rem;
}
</style>
